import { dispatcher } from 'dispatcher';
import { EventTarget } from 'event-target-shim/es5';
import { useEffect, useRef } from 'react';

import * as hooks from '@core/js/hooks/usePublicAPI/hooks/useEventsAPI/hooks';
import { useOnVerificationEmail } from '@theme/js/hooks/usePublicAPI/hooks/useEventsAPI/hooks/useOnVerificationEmail';
import { PLAYTECH_API_EVENTS } from './constants';

export const useEventsAPI = () => {
    /**
     * This object is just an EventTarget, that allows to subscribe to events.
     * See [MDN docs]{@link https://developer.mozilla.org/en-US/docs/Web/API/EventTarget}.
     * @memberOf Playtech.API
     * @type {EventTarget}
     * @example
     * Playtech.on('eventName', (...args) => { /* ... * / });
     * @example
     * Playtech.once('eventName', (...args) => { /* ... * / });
     * @example
     * Playtech.off('eventName', (...args) => { /* ... * / });
     */
    const eventTarget = useRef(new EventTarget());
    const handlers = useRef(new WeakMap());
    // eslint-disable-next-line no-underscore-dangle
    const { on = [], once = [] } = window.Playtech._API || {};

    hooks.useOnIpChange(eventTarget.current);
    hooks.useOnNavigate(eventTarget.current);
    hooks.useOnCookieSettingsUpdated(eventTarget.current);
    hooks.useOnCookieMessageClosed(eventTarget.current);
    hooks.useOnHub2SharedPagesEventsTrack(eventTarget.current);
    hooks.useOnPlayerTagChange(eventTarget.current);
    hooks.useOnRealityCheckPopup(eventTarget.current);
    hooks.useOnGameLaunch(eventTarget.current);
    hooks.useOn2FAPopupClosed(eventTarget.current);
    hooks.useOn2FAPopupOpened(eventTarget.current);
    hooks.useOnUserToggleBalance(eventTarget.current);
    hooks.useOnUserLowBalanceCheck(eventTarget.current);
    hooks.useOnBackButtonToggle(eventTarget.current);
    hooks.useOnBankIDAuthRegisterSuccess(eventTarget.current);
    hooks.useOnSelfExclusion(eventTarget.current);
    hooks.useOnForgotUsername(eventTarget.current);
    hooks.useOnForgotPassword(eventTarget.current);
    hooks.useOnUserBalanceUpdated(eventTarget.current);
    hooks.useOnSportsbookAnalytics(eventTarget.current);
    hooks.useOnRegistrationSubmitted(eventTarget.current);
    useOnVerificationEmail(eventTarget.current);

    useEffect(() => {
        window.Playtech = window.Playtech || {};
        window.Playtech = {
            ...window.Playtech,

            /**
             * Shortcut for Playtech.API.events.addEventListener(event, ({ detail }) => callback(detail)).
             * @memberOf Playtech
             * @name on
             * @function
             * @param event {String}
             * @param callback {EventTarget.EventListener}
             * @example
             * window.Playtech.on(event, ({ detail }) => callback(detail));
             */
            on: (event, callback) => {
                const handler = ({ detail }) => callback(detail);

                handlers.current.set(callback, handler);
                eventTarget.current.addEventListener(event, handler);
            },

            /**
             * Shortcut for Playtech.API.events.addEventListener(event, ({ detail }) => callback(detail), { once: true }).
             * @memberOf Playtech
             * @name once
             * @function
             * @param event {String}
             * @param callback {EventTarget.EventListener}
             * @example
             * window.Playtech.once(event, ({ detail }) => callback(detail));
             */
            once: (event, callback) => {
                const handler = ({ detail }) => callback(detail);

                handlers.current.set(callback, handler);
                eventTarget.current.addEventListener(event, handler, { once: true });
            },

            /**
             * Shortcut for Playtech.API.events.removeEventListener(event, callback).
             * @memberOf Playtech
             * @name off
             * @function
             * @param event {String}
             * @param callback {EventTarget.EventListener}
             * @example
             * window.Playtech.off(event, callback);
             */
            off: (event, callback) => {
                const handler = handlers.current.get(callback);

                eventTarget.current.removeEventListener(event, handler ?? callback);
            }
        };
    }, []);

    useEffect(() => {
        on.forEach(({ event, callback }) => window.Playtech.on(event, callback));
        once.forEach(({ event, callback }) => window.Playtech.once(event, callback));
    }, [
        on,
        once
    ]);

    useEffect(() => {
        Object.keys(PLAYTECH_API_EVENTS)
            .forEach((key) => {
                dispatcher.listen(PLAYTECH_API_EVENTS[key], (...args) => {
                    eventTarget.current.dispatchEvent(new CustomEvent(key, {
                        detail: args
                    }));
                });
            });
    }, []);

    return eventTarget.current;
};
